
import React, { useState } from 'react';
import { X, AlertTriangle, ShieldCheck, Flag, Info, MessageSquare, ShieldAlert } from 'lucide-react';
import { useAppContext } from '../../App.jsx';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { PROPOSALS_COLLECTION } from '../../config/constants';

const REPORT_REASONS = [
    { id: 'cancel_request', label: 'Solicitar Cancelamento (Serviço Aceito)', category: 'support' },
    { id: 'location_error', label: 'Não encontro o local / Endereço errado', category: 'support' },
    { id: 'no_show', label: 'Não comparecimento / Atraso grave', category: 'support' },
    { id: 'harassment', label: 'Assédio ou Linguagem Ofensiva', category: 'security' },
    { id: 'fraud', label: 'Tentativa de Fraude ou Golpe', category: 'security' },
    { id: 'other', label: 'Outro motivo', category: 'security' }
];

const ReportModal = ({ chatId, targetUserId, targetUserName, serviceTitle, onClose }) => {
    const { user, db, showMessage, activeRole, fullUserData } = useAppContext();
    const [selectedReason, setSelectedReason] = useState('');
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const currentReasonObj = REPORT_REASONS.find(r => r.id === selectedReason);
    const isSupportCategory = currentReasonObj?.category === 'support';

    const handleSubmit = async () => {
        if (!selectedReason) {
            showMessage("Por favor, selecione o motivo.", "warning");
            return;
        }

        if (description.length < 10) {
            showMessage("Descreva o ocorrido com pelo menos 10 caracteres.", "warning");
            return;
        }

        setIsSubmitting(true);
        try {
            const reporterName = fullUserData?.profile_client?.name || fullUserData?.profile_servi?.name || "Usuário";

            const q = query(
                collection(db, PROPOSALS_COLLECTION),
                where('chatId', '==', chatId),
                where('status', '==', 'accepted')
            );
            const snap = await getDocs(q);
            const hasAcceptedProposal = !snap.empty;

            await addDoc(collection(db, 'reports'), {
                chatId,
                serviceTitle: serviceTitle || "Não informado",
                hasAcceptedProposal,
                category: currentReasonObj.category,

                reporterId: user.uid,
                reporterName: reporterName,
                reporterRole: activeRole,

                reportedUserId: targetUserId,
                reportedUserName: targetUserName || "Não informado",
                reportedUserRole: activeRole === 'servi' ? 'client' : 'servi',

                reason: selectedReason,
                description,
                status: 'pending',
                type: (isSupportCategory && hasAcceptedProposal) ? 'critical_cancel' : 'standard_report',
                createdAt: serverTimestamp()
            });

            const successMsg = isSupportCategory
                ? "Solicitação enviada! Nossa equipe iniciará um chat de suporte exclusivo com você em breve."
                : "Relato de segurança enviado! Nossa equipe de moderação analisará o caso e aplicará as medidas cabíveis conforme nossos termos de uso.";

            showMessage(successMsg, isSupportCategory ? "success" : "info");

            setTimeout(() => {
                onClose();
            }, 2500);

        } catch (error) {
            console.error("Erro ao enviar reporte:", error);
            showMessage("Erro técnico ao enviar. Tente novamente.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[999] flex items-center justify-center p-4 transition-all"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-[2rem] w-full max-w-md overflow-hidden shadow-2xl animate-slide-up flex flex-col"
                style={{ maxHeight: '90vh' }}
                onClick={(e) => e.stopPropagation()}
            >
                <header className={`p-5 border-b flex justify-between items-center shrink-0 ${isSupportCategory ? 'bg-blue-50' : 'bg-red-50'}`}>
                    <div className={`flex items-center ${isSupportCategory ? 'text-blue-600' : 'text-red-600'}`}>
                        {isSupportCategory ? <MessageSquare size={20} className="mr-2" /> : <AlertTriangle size={20} className="mr-2" />}
                        <h2 className="font-black text-sm uppercase tracking-widest">
                            {isSupportCategory ? 'Suporte ao Serviço' : 'Denunciar Usuário'}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-3 bg-white border border-gray-100 hover:bg-gray-100 rounded-full transition-all shadow-sm active:scale-90"
                    >
                        <X size={22} className="text-gray-500" />
                    </button>
                </header>

                <div className="p-6 space-y-6 overflow-y-auto">
                    {selectedReason && (
                        <div className={`p-4 rounded-2xl border flex items-start animate-fade-in ${isSupportCategory ? 'bg-blue-50 border-blue-100' : 'bg-amber-50 border-amber-100'}`}>
                            {isSupportCategory ? (
                                <Info size={18} className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                            ) : (
                                <ShieldAlert size={18} className="text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
                            )}
                            <p className={`text-[11px] leading-relaxed font-medium ${isSupportCategory ? 'text-blue-800' : 'text-amber-800'}`}>
                                {isSupportCategory
                                    ? "Para este caso, a T-SERVI poderá abrir um canal de mediação via chat para ajudar na resolução do problema."
                                    : "Denúncias de segurança são analisadas privadamente pela moderação. Não haverá mediação de conversa entre as partes por segurança."
                                }
                            </p>
                        </div>
                    )}

                    <div>
                        <label className="block text-[10px] font-black text-gray-400 mb-3 uppercase tracking-widest ml-1">Selecione o motivo:</label>
                        <div className="grid grid-cols-1 gap-2">
                            {REPORT_REASONS.map(reason => (
                                <button
                                    key={reason.id}
                                    onClick={() => setSelectedReason(reason.id)}
                                    className={`px-4 py-3 rounded-xl text-xs font-bold text-left transition-all border-2 ${
                                        selectedReason === reason.id
                                        ? (isSupportCategory ? 'bg-blue-600 border-blue-600 text-white' : 'bg-red-600 border-red-600 text-white')
                                        : 'bg-white border-gray-100 text-gray-600 hover:border-blue-200'
                                    }`}
                                >
                                    {reason.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] font-black text-gray-400 mb-2 uppercase tracking-widest ml-1">Detalhes do ocorrido:</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Descreva o que aconteceu..."
                            className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl h-32 focus:ring-2 focus:ring-blue-400 focus:bg-white focus:outline-none transition-all text-sm shadow-inner"
                        />
                    </div>
                </div>

                <footer className="p-6 bg-gray-50 border-t flex flex-col gap-3 shrink-0">
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className={`w-full py-4 text-white font-black text-xs rounded-2xl shadow-xl transition-all active:scale-[0.98] uppercase tracking-widest flex items-center justify-center disabled:bg-gray-300 ${isSupportCategory ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'}`}
                    >
                        {isSubmitting ? 'PROCESSANDO...' : (
                            <>
                                <Flag size={16} className="mr-2" /> {isSupportCategory ? 'SOLICITAR AJUDA' : 'ENVIAR DENÚNCIA'}
                            </>
                        )}
                    </button>
                    <div className="flex items-center justify-center text-[10px] text-gray-400 font-bold uppercase tracking-tighter">
                        <ShieldCheck size={12} className="mr-1 text-green-500" /> Proteção e Moderação T-SERVI
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default ReportModal;
